import { Box, Typography } from '@mui/material';
import { Link, Navigate } from 'react-router-dom';

import { useAuth } from '../../core/authContext';
import { outlinedButton, primaryButton, Shell, useReportQuery } from '../ImplementationReport';
import { localToday } from '../ImplementationReport/period';
import { USAGE } from '../ImplementationReport/tokens';
import { fetchAssignmentLibrary, viewerZone, type LibraryAssignment, type LibrarySource } from './api';
import { LIBRARY_ROUTE, LoadState, PromptPicture, readyOf, useLibrary, type Ready } from './index';
import { domainsOf, gradeLabel, longDayLabel, picksKey, printPages, promptBlocks, readPicks, schoolNames, teacherName, typeLabel, type PrintPiece } from './rows';
import { LIBRARY } from './tokens';

export const printStyles = `@page { size: letter; margin: 0; }
@media print {
  [data-print="hide"], [data-testid="lib-print-bar"] { display: none !important; }
  [data-print="flat"] { padding: 0 !important; gap: 0 !important; max-width: none !important; }
  [data-testid="lib-print-pages"] { gap: 0 !important; }
  [data-testid="lib-print-page"] { box-shadow: none !important; page-break-after: always; break-after: page; }
  [data-testid="lib-print-entry"] { break-inside: avoid; page-break-inside: avoid; }
}`;

const font = { fontFamily: USAGE.font };
const caption = { ...font, fontSize: 12, fontWeight: 700, lineHeight: '16px', letterSpacing: '0.5px', textTransform: 'uppercase' as const, color: USAGE.muted };

function Entry({ piece, ready }: { piece: PrintPiece; ready: Ready }) {
  const a = piece.assignment;
  const domains = domainsOf(a);
  const blocks = piece.blocks;
  const labelled = promptBlocks(a).length > 1;
  return (
    <Box data-testid="lib-print-entry" data-continued={String(piece.continued)} sx={{ display: 'flex', gap: '24px', pb: '24px', boxShadow: `inset 0 -1px 0 ${USAGE.divider}` }}>
      <Typography data-testid="lib-print-number" sx={{ flex: '0 0 32px', ...font, fontSize: 24, fontWeight: 700, lineHeight: '32px', color: USAGE.muted }}>{piece.number}</Typography>
      <Box sx={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
          <Typography data-testid="lib-print-title" sx={{ ...font, fontSize: 20, fontWeight: 700, lineHeight: '32px', color: USAGE.ink }}>{piece.continued ? `${a.title} (continued)` : a.title}</Typography>
          <Typography component="small" sx={caption}>{[typeLabel(a), ...domains].join(' · ')}</Typography>
        </Box>
        {blocks.map((block, i) => (
          <Box key={`${block.label}-${i}`} data-testid="lib-print-prompt" sx={{ display: 'flex', gap: '12px' }}>
            {labelled && <Typography component="small" sx={{ ...caption, flex: '0 0 72px', pt: '4px', lineHeight: '20px' }}>{block.domains[0]}</Typography>}
            <Typography sx={{ flex: '1 1 0', ...font, fontSize: 20, fontWeight: 400, lineHeight: '28px', color: USAGE.ink, whiteSpace: 'pre-wrap' }}>{block.text}</Typography>
            <PromptPicture file={block.file} alt={`${block.label} picture`} width={160} />
          </Box>
        ))}
        <Typography data-testid="lib-print-by" sx={{ ...font, fontSize: 16, fontWeight: 500, lineHeight: '24px', color: USAGE.muted }}>
          {[teacherName(a), schoolNames(a, ready.report.schools), gradeLabel(a.grades)].filter((f) => f !== '').join(' · ')}
        </Typography>
      </Box>
    </Box>
  );
}

export function LibraryPrintPage({
  source = fetchAssignmentLibrary,
  print = () => window.print(),
  today = localToday(),
  zone = viewerZone(),
  printedOn = localToday(),
}: {
  source?: LibrarySource;
  print?: () => void;
  today?: string;
  zone?: string;
  printedOn?: string;
}) {
  const { isDistrictAdmin, isSuperAdmin } = useAuth();
  const query = useReportQuery();
  const allowed = isDistrictAdmin || isSuperAdmin;
  const picks = readPicks(picksKey(query.granularity, query.start));
  const loaded = useLibrary(source, { granularity: query.granularity, start: query.start, today, tz: zone }, allowed && picks.length > 0);
  if (!allowed) return <Navigate to="/reports" replace />;
  if (picks.length === 0) return <Navigate to={LIBRARY_ROUTE + query.search} replace />;
  const ready = loaded.kind === 'ready' ? readyOf(loaded.data) : null;
  const entries = ready ? picks.map((id) => ready.report.assignments.find((a) => a.id === id)).filter((a): a is LibraryAssignment => a !== undefined) : [];
  const pages = printPages(entries, LIBRARY.print.entryHeight, LIBRARY.print.entryHeight - LIBRARY.print.titleBlock);
  const back = LIBRARY_ROUTE + query.search;
  return (
    <Shell crumbs={[{ label: 'Reports', to: '/reports' }, { label: 'Assignment library', to: back }, { label: 'Print selected' }]}>
      <style>{printStyles}</style>
      <Box data-testid="lib-print-bar" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
        <Box component={Link} to={back} sx={outlinedButton}>Back</Box>
        <Box component="button" type="button" onClick={print} sx={{ ...primaryButton, '&:disabled': { opacity: 0.5, cursor: 'default' } }} disabled={entries.length === 0}>Print</Box>
      </Box>
      <LoadState loaded={loaded} />
      {ready && entries.length === 0 && <Typography sx={{ ...font, fontSize: 16, fontWeight: 700, color: USAGE.ink }}>None of the selected assignments are in this period.</Typography>}
      {ready && entries.length > 0 && (
        <Box data-testid="lib-print-pages" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px' }}>
          {pages.map((page, index) => (
            <Box key={index} data-testid="lib-print-page" component="article" sx={{ width: LIBRARY.print.width, height: 'auto', minHeight: LIBRARY.print.height, overflow: 'visible', boxSizing: 'border-box', p: `${LIBRARY.print.padding}px`, bgcolor: USAGE.cardBg, boxShadow: '0 2px 8px rgba(8, 8, 27, 0.12)', display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {index === 0 && (
                <Box>
                  <Typography component="h1" sx={{ ...font, fontSize: 32, fontWeight: 800, lineHeight: '40px', letterSpacing: '-0.5px', color: USAGE.ink }}>Assignment examples</Typography>
                  <Typography data-testid="lib-print-dates" sx={{ ...font, fontSize: 16, fontWeight: 500, lineHeight: '24px', color: USAGE.muted }}>{`${ready.periodShort} · ${entries.length} ${entries.length === 1 ? 'assignment' : 'assignments'}`}</Typography>
                </Box>
              )}
              {page.map((piece, i) => <Entry key={`${piece.assignment.id}-${i}`} piece={piece} ready={ready} />)}
              <Typography data-testid="lib-print-footer" sx={{ mt: 'auto', textAlign: 'right', whiteSpace: 'nowrap', ...font, fontSize: 12, lineHeight: '16px', color: USAGE.muted }}>{`Printed ${longDayLabel(printedOn)} · Page ${index + 1} of ${pages.length}`}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Shell>
  );
}
